// OCPI roaming console (maintenance). Base = /api/maintenance/ocpi.
//
// Partners ("agents") are external platforms that reach our charging network over OCPI 2.2.1:
// map apps, national access points, roaming eMSPs and hubs. Nobody gets blanket access:
// every partner is assigned an ACCESS ROLE (a permission group with default rate limits),
// optionally tweaked per partner with ALLOW/DENY overrides and limit overrides.
//
// Keys: the partner's registration key (OCPI "Token A") is shown exactly once when issued;
// the service stores only hashes. After the partner registers, OCPI rotates to a working key
// that never leaves the OCPI handshake, so there is nothing further to copy.
import axiosInstance from "../axiosInstance";

const BASE = "/api/maintenance/ocpi";

export type RegistrationState = "NEW" | "PENDING" | "REGISTERED" | "UNREGISTERED";
export type LocationScope = "ALL" | "SELECTED" | "NONE";

export interface OcpiPermission {
  permission: string;
  module: string;
  label: string;
  description: string;
  implemented: boolean;
  sort_order: number;
}

export interface AccessRole {
  id: number;
  role_key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  intended_party_roles: string[];
  requests_per_minute: number;
  burst: number;
  commands_per_minute: number;
  max_page_size: number;
  log_bodies: boolean;
  is_archived: boolean;
  permissions: string[];
  partner_count: number;
  updated_at: string;
  updated_by: string | null;
}

export interface AccessRoleInput {
  name: string;
  description?: string | null;
  intended_party_roles?: string[];
  permissions?: string[];
  requests_per_minute?: number;
  burst?: number;
  commands_per_minute?: number;
  max_page_size?: number;
  log_bodies?: boolean;
  clone_from_role_id?: number | null;
  is_archived?: boolean;
}

export interface PartnerRow {
  id: number;
  partner_guid: string;
  name: string;
  registration_state: RegistrationState;
  is_suspended: boolean;
  suspended_reason: string | null;
  ocpi_version: string | null;
  location_scope: LocationScope;
  created_at: string;
  registered_at: string | null;
  last_seen_at: string | null;
  access_role_id: number;
  role_key: string;
  role_name: string;
  parties: string;
  requests_24h: number;
  errors_24h: number;
}

export interface Partner {
  id: number;
  partner_guid: string;
  name: string;
  access_role_id: number;
  registration_state: RegistrationState;
  is_suspended: boolean;
  suspended_reason: string | null;
  ocpi_version: string | null;
  versions_url: string | null;
  token_b_hint: string | null;
  outbound_token_base64: boolean;
  requests_per_minute: number | null;
  burst: number | null;
  commands_per_minute: number | null;
  max_page_size: number | null;
  log_bodies: boolean | null;
  allowed_ips: string[];
  location_scope: LocationScope;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  cms_org_id: number | null;
  cms_user_id: number | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  registered_at: string | null;
  last_seen_at: string | null;
  has_outbound_credentials: boolean;
}

export interface PartnerCreateInput {
  name: string;
  access_role_id: number;
  contact_name?: string | null;
  contact_email?: string | null;
  notes?: string | null;
  location_scope?: LocationScope;
  allowed_ips?: string[] | null;
}

export type PartnerUpdateInput = Partial<Omit<Partner, "id" | "partner_guid" | "registration_state" | "is_suspended">>;

export interface TokenMeta {
  id: number;
  kind: "A" | "C";
  token_hint: string;
  status: "ACTIVE" | "USED" | "REVOKED" | "EXPIRED";
  expires_at: string | null;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
  revoke_reason: string | null;
}

export interface PartnerJob {
  id: number;
  job_type: string;
  params: Record<string, any> | null;
  state: "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED";
  result: Record<string, any> | null;
  error: string | null;
  requested_by: string | null;
  requested_at: string;
  finished_at: string | null;
}

export interface PartnerDetail {
  partner: Partner;
  role: AccessRole;
  overrides: { permission: string; effect: "ALLOW" | "DENY" }[];
  effective_permissions: string[];
  effective_limits: { requests_per_minute: number; burst: number; commands_per_minute: number; max_page_size: number; log_bodies: boolean };
  parties: { role: string; country_code: string; party_id: string; business_details: any }[];
  endpoints: { version: string; module: string; interface_role: string; url: string; fetched_at: string }[];
  tokens: TokenMeta[];
  stations: number[];
  events: { id: number; event: string; detail: any; actor: string | null; at: string }[];
  jobs: PartnerJob[];
  cms_identity: { org_id: number; org_name: string; user_id: number | null } | null;
  versions_url: string;
}

export interface RegistrationKey {
  token: string;
  token_hint: string;
  versions_url: string;
  expires_at: string;
}

export interface LogRow {
  id: number;
  created_at: string;
  partner_id: number | null;
  partner_name: string | null;
  direction: "IN" | "OUT";
  method: string;
  url: string;
  module: string | null;
  http_status: number | null;
  ocpi_status: number | null;
  duration_ms: number | null;
  request_id: string | null;
  correlation_id: string | null;
  from_party: string | null;
  to_party: string | null;
  client_ip: string | null;
  token_kind: string | null;
  token_hint: string | null;
  rate_limited: boolean;
  request_bytes: number | null;
  response_bytes: number | null;
  error: string | null;
}

export interface LogEntry extends LogRow {
  request_headers: Record<string, any> | null;
  request_body: string | null;
  response_body: string | null;
}

export interface LogFilters {
  partner_id?: number;
  before_id?: number | null;
  start?: string;
  end?: string;
  direction?: "" | "IN" | "OUT";
  module?: string;
  outcome?: "" | "ok" | "client" | "server" | "limited" | "auth";
  q?: string;
  limit?: number;
}

export interface MetricsPoint {
  bucket: string;
  total: number;
  ok: number;
  client_errors: number;
  server_errors: number;
  rate_limited: number;
  p95_ms: number | null;
}

export interface Metrics {
  start: string;
  end: string;
  bucket_seconds: number;
  series: MetricsPoint[];
  summary: { total: number; errors: number; rate_limited: number; auth_failures: number; p50_ms: number | null; p95_ms: number | null };
  by_module: { module: string; total: number; errors: number; avg_ms: number | null }[];
  outbound: { total: number; failed: number };
}

export interface OcpiSettings {
  country_code: string;
  party_id: string | null;
  business_name: string;
  business_website: string | null;
  logo_url: string | null;
  logo_thumbnail_url: string | null;
  logo_type: string | null;
  logo_width: number | null;
  logo_height: number | null;
  evse_id_prefix: string | null;
  default_time_zone: string;
  terms_and_conditions_url: string | null;
  session_start_enabled: boolean;
  updated_at: string;
  updated_by: string | null;
}

export interface Overview {
  counts: {
    partners: number; registered: number; suspended: number; locations: number; evses: number;
    active_sessions: number; cdrs_30d: number; requests_24h: number; connector_types_to_review: number;
  };
  service: Record<string, any> | null;
  settings: OcpiSettings;
  versions_url: string;
}

export interface OpeningHours {
  twentyfourseven: boolean;
  regular_hours: { weekday: number; period_begin: string; period_end: string }[];
}

export interface LocationRow {
  charging_station_id: number;
  station_name: string;
  text_address: string | null;
  is_active: boolean;
  station_type: string | null;
  ocpi_location_id: string | null;
  publish: boolean;
  name: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  state: string | null;
  parking_type: string | null;
  facilities: string[];
  opening_times: OpeningHours | null;
  charging_when_closed: boolean | null;
  directions: { language: string; text: string }[] | null;
  time_zone: string | null;
  is_published: boolean;
  published_city: string | null;
  published_postal_code: string | null;
  evse_count: number;
  last_updated: string | null;
}

export interface ConnectorTypeRow {
  socket_type_id: number;
  socket_name: string;
  charging_current_mode: string;
  standard: string | null;
  format: "SOCKET" | "CABLE" | null;
  power_type: string | null;
  max_voltage: number | null;
  max_amperage: number | null;
  publish: boolean;
  needs_review: boolean;
  mapped: boolean;
  connectors: number;
  updated_by: string | null;
}

export interface OcpiSessionRow {
  id: string; status: string; location_id: string; evse_uid: string; connector_id: string;
  start_date_time: string; end_date_time: string | null; kwh: string; total_cost_excl: string | null;
  total_cost_incl: string | null; token_uid: string; authorization_reference: string | null;
  charging_session_id: number; last_updated: string;
}

export interface OcpiCdrRow {
  id: string; session_id: string; is_credit: boolean; start_date_time: string; end_date_time: string;
  total_energy: string; total_cost_excl: string; total_cost_incl: string | null; last_updated: string;
}

export interface OcpiCommandRow {
  id: number; command: string; location_id: string | null; evse_uid: string | null; connector_id: string | null;
  session_id: string | null; response_result: string; response_message: string | null; state: string;
  result: string | null; result_message: string | null; delivery_attempts: number; last_delivery_error: string | null;
  created_at: string; result_at: string | null; delivered_at: string | null;
}

export const ocpiApi = {
  overview: () => axiosInstance.get<Overview>(`${BASE}/overview`),
  settings: () => axiosInstance.get<OcpiSettings>(`${BASE}/settings`),
  updateSettings: (data: Partial<OcpiSettings>) => axiosInstance.put<OcpiSettings>(`${BASE}/settings`, data),
  permissions: () => axiosInstance.get<OcpiPermission[]>(`${BASE}/permissions`),

  roles: () => axiosInstance.get<AccessRole[]>(`${BASE}/roles`),
  createRole: (data: AccessRoleInput) => axiosInstance.post<AccessRole>(`${BASE}/roles`, data),
  updateRole: (id: number, data: Partial<AccessRoleInput>) => axiosInstance.put<AccessRole>(`${BASE}/roles/${id}`, data),
  deleteRole: (id: number) => axiosInstance.delete(`${BASE}/roles/${id}`),

  partners: (search = "", state = "") =>
    axiosInstance.get<PartnerRow[]>(`${BASE}/partners`, { params: { search: search || undefined, state: state || undefined } }),
  partner: (id: number) => axiosInstance.get<PartnerDetail>(`${BASE}/partners/${id}`),
  createPartner: (data: PartnerCreateInput) => axiosInstance.post<Partner>(`${BASE}/partners`, data),
  updatePartner: (id: number, data: PartnerUpdateInput) => axiosInstance.patch<Partner>(`${BASE}/partners/${id}`, data),
  setOverrides: (id: number, overrides: { permission: string; effect: "ALLOW" | "DENY" }[]) =>
    axiosInstance.put(`${BASE}/partners/${id}/permissions`, { overrides }),
  setStations: (id: number, charging_station_ids: number[]) =>
    axiosInstance.put(`${BASE}/partners/${id}/locations`, { charging_station_ids }),
  suspend: (id: number, reason: string) => axiosInstance.post<Partner>(`${BASE}/partners/${id}/suspend`, { reason }),
  resume: (id: number) => axiosInstance.post<Partner>(`${BASE}/partners/${id}/resume`),
  /** Returns the registration key ONCE. It is not retrievable afterwards. */
  issueRegistrationKey: (id: number) => axiosInstance.post<RegistrationKey>(`${BASE}/partners/${id}/registration-token`),
  revoke: (id: number, reason: string) => axiosInstance.post(`${BASE}/partners/${id}/revoke`, { reason }),
  createJob: (id: number, job_type: string, versions_url?: string, token?: string) =>
    axiosInstance.post<{ job_id: number; state: string }>(`${BASE}/partners/${id}/jobs`, { job_type, versions_url, token }),
  job: (id: number, jobId: number) => axiosInstance.get<PartnerJob>(`${BASE}/partners/${id}/jobs/${jobId}`),
  provisionCms: (id: number) => axiosInstance.post(`${BASE}/partners/${id}/provision-cms`),
  sessions: (id: number) => axiosInstance.get<OcpiSessionRow[]>(`${BASE}/partners/${id}/sessions`),
  cdrs: (id: number) => axiosInstance.get<OcpiCdrRow[]>(`${BASE}/partners/${id}/cdrs`),
  commands: (id: number) => axiosInstance.get<OcpiCommandRow[]>(`${BASE}/partners/${id}/commands`),

  logs: (f: LogFilters) =>
    axiosInstance.get<{ items: LogRow[]; next_before_id: number | null }>(`${BASE}/logs`, {
      params: {
        partner_id: f.partner_id, before_id: f.before_id || undefined, start: f.start || undefined, end: f.end || undefined,
        direction: f.direction || undefined, module: f.module || undefined, outcome: f.outcome || undefined,
        q: f.q || undefined, limit: f.limit ?? 100,
      },
    }),
  log: (id: number) => axiosInstance.get<LogEntry>(`${BASE}/logs/${id}`),
  metrics: (partnerId?: number, start?: string, end?: string) =>
    axiosInstance.get<Metrics>(`${BASE}/metrics`, { params: { partner_id: partnerId, start, end } }),

  locations: () => axiosInstance.get<LocationRow[]>(`${BASE}/locations`),
  updateLocation: (stationId: number, data: Record<string, any>) => axiosInstance.put(`${BASE}/locations/${stationId}`, data),
  connectorTypes: () => axiosInstance.get<ConnectorTypeRow[]>(`${BASE}/connector-types`),
  updateConnectorType: (socketTypeId: number, data: Record<string, any>) =>
    axiosInstance.put(`${BASE}/connector-types/${socketTypeId}`, data),
};
